import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Ban, KeyRound } from 'lucide-react';
import api from '../../services/api';

type AdminUser = {
  _id: string;
  name: string;
  email: string;
  role: string;
  isActive?: boolean;
  isBanned?: boolean;
  banReason?: string;
  avatar?: string;
};

interface AdminUserEditModalProps {
  user: AdminUser;
  onClose: () => void;
  onUpdated: () => void;
}

const AdminUserEditModal: React.FC<AdminUserEditModalProps> = ({ user, onClose, onUpdated }) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState('');
  const [banReason, setBanReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(user.name);
    setEmail(user.email);
    setPassword('');
    setBanReason('');
    setMessage(null);
    setError(null);
  }, [user]);

  const saveProfile = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.adminUpdateUser(user._id, { name, email });
      setMessage('Bilgiler güncellendi');
      onUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async () => {
    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalı');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.adminSetUserPassword(user._id, password);
      setPassword('');
      setMessage('Şifre güncellendi');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Şifre güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const banUser = async () => {
    if (!window.confirm(`${user.name} hesabını kapatmak istediğinize emin misiniz?`)) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.adminBanUser(user._id, banReason.trim() || undefined);
      setMessage('Hesap kapatıldı ve e-posta/cihaz engellendi');
      onUpdated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Hesap kapatılamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="glow-card"
        style={{ width: '100%', maxWidth: 480, padding: 24, borderRadius: 16 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={user.avatar} alt="" style={{ width: 44, height: 44, borderRadius: 10 }} />
            <div>
              <h3 style={{ margin: 0, color: '#f8fafc' }}>Kullanıcı Düzenle</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>{user.role === 'admin' ? 'Admin' : 'Kullanıcı'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {user.isBanned && (
          <div style={{ marginBottom: 12, padding: 10, borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: 13 }}>
            Kapatılmış hesap{user.banReason ? `: ${user.banReason}` : ''}
          </div>
        )}

        <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Ad Soyad</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />

        <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, margin: '12px 0 6px' }}>E-posta</label>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={saveProfile} disabled={loading} style={primaryBtn}>
            <Save size={14} /> Kaydet
          </button>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid rgba(51,65,85,0.4)', margin: '20px 0' }} />

        <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Yeni şifre</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="En az 6 karakter"
          style={inputStyle}
        />
        <button type="button" onClick={savePassword} disabled={loading || password.length < 6} style={{ ...secondaryBtn, marginTop: 10 }}>
          <KeyRound size={14} /> Şifreyi Güncelle
        </button>

        {user.role !== 'admin' && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(51,65,85,0.4)', margin: '20px 0' }} />
            <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Kapatma nedeni</label>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              rows={3}
              placeholder="İsteğe bağlı — e-posta ve cihaz engellenir"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <button type="button" onClick={banUser} disabled={loading} style={{ ...dangerBtn, marginTop: 10 }}>
              <Ban size={14} /> Hesabı Kapat ve Engelle
            </button>
          </>
        )}

        {message && <p style={{ color: '#4ade80', fontSize: 13, marginTop: 12 }}>{message}</p>}
        {error && <p style={{ color: '#f87171', fontSize: 13, marginTop: 12 }}>{error}</p>}
      </motion.div>
    </motion.div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(51,65,85,0.45)',
  background: 'rgba(15,23,42,0.6)',
  color: '#e2e8f0',
  fontSize: 14,
};

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 14px',
  borderRadius: 10,
  border: 'none',
  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
  color: 'white',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  background: 'rgba(51,65,85,0.5)',
  color: '#cbd5e1',
};

const dangerBtn: React.CSSProperties = {
  ...primaryBtn,
  background: 'rgba(239,68,68,0.15)',
  color: '#f87171',
  border: '1px solid rgba(239,68,68,0.35)',
};

export default AdminUserEditModal;
